<template>
  <div>
    <div class="main-wrapper" v-if="!isLoginScreen">
      <LeftMenu :menu="menu" @drawerHold="holdValue" @drawerStateChanged="handleDrawerStateChange" />
      <div class="right-wrapper" :style="rightSubContentStyle">
        <Navbar />
        <div class="right-sub-content">
          <router-view v-slot="{ Component, route }">
            <template v-if="route.matched.length && Component">
              <component :is="Component" :key="route.path" />
            </template>
            <div v-else-if="route.matched.length" class="route-view-page-loading" role="status">
              <p>Loading page…</p>
            </div>
            <teleport v-else to="body">
              <div class="route-view-fallback route-view-fallback--portal">
                <p>Unable to load this page.</p>
                <p class="route-view-fallback-path">{{ route.fullPath }}</p>
                <v-btn color="primary" class="mt-3" type="button" @click.stop="recoverRoute(route)">
                  Go to home
                </v-btn>
              </div>
            </teleport>
          </router-view>
        </div>
      </div>
    </div>
    <router-view v-if="isLoginScreen" />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import Navbar from "./Navbar.vue";
import LeftMenu from "./LeftMenu.vue";
import screenWidthMixin from "../../../assets/JS/screenWidthMixin";
import { resolveTenantIdFromBrowser } from "@/utils/tenant";

export default {
  name: "UniversalLayout",
  data() {
    return {
      menu: true,
    };
  },
  components: {
    Navbar,
    LeftMenu,
  },
  mixins: [screenWidthMixin],
  computed: {
    ...mapGetters("tenantId", ["getSelectedTenant"]),
    isLoginScreen() {
      return this.$route.name === "Login";
    },
  },
  watch: {
    $route: {
      handler() {
        this.scheduleUnmatchedRouteCheck();
      },
    },
  },
  methods: {
    holdValue(drawer) {
      this.menu = drawer;
    },
    resolveRecoveryTenantId() {
      return (
        resolveTenantIdFromBrowser() ||
        (this.getSelectedTenant?.ID != null ? String(this.getSelectedTenant.ID) : null)
      );
    },
    recoverRoute(route) {
      const tenantId = this.resolveRecoveryTenantId();
      if (!tenantId) {
        this.$router.push("/login");
        return;
      }
      const target = this.resolveRecoveryTarget(route, tenantId);
      this.$router.replace(target);
    },
    resolveRecoveryTarget(route, tenantId) {
      const lastView = localStorage.getItem("lastView");
      if (lastView && String(lastView).startsWith("/")) {
        const lastPath = String(lastView).split("?")[0];
        const lastSeg = lastPath.split("/").filter(Boolean);
        if (lastSeg[0] === tenantId && lastSeg.length > 1) {
          return lastView;
        }
      }
      const path = route?.path || "";
      const segments = path.split("/").filter(Boolean);
      const rest =
        segments.length > 0 && segments[0] === tenantId
          ? segments.slice(1)
          : segments;
      if (rest.length > 0) {
        return `/${tenantId}/${rest.join("/")}`;
      }
      return `/${tenantId}/data-management`;
    },
    scheduleUnmatchedRouteCheck() {
      if (this._unmatchedRouteTimer) {
        clearTimeout(this._unmatchedRouteTimer);
      }
      this._unmatchedRouteTimer = setTimeout(() => {
        this.tryRecoverUnmatchedRoute();
      }, 0);
    },
    tryRecoverUnmatchedRoute() {
      if (this.isLoginScreen) return;
      if (!localStorage.getItem("authToken")) return;

      const runCheck = () => {
        if (this.$route.matched.length > 0) return;
        const tenantId = this.resolveRecoveryTenantId();
        if (!tenantId) return;
        const target = this.resolveRecoveryTarget(this.$route, tenantId);
        const current = this.$route.fullPath.split("?")[0];
        const targetPath = target.split("?")[0];
        if (targetPath === current) return;
        this.$router.replace(target).catch(() => {});
      };

      if (this.$router.isReady) {
        this.$router.isReady().then(runCheck).catch(runCheck);
      } else {
        runCheck();
      }
    },
  },
  mounted() {
    this.scheduleUnmatchedRouteCheck();
  },
  beforeUnmount() {
    if (this._unmatchedRouteTimer) {
      clearTimeout(this._unmatchedRouteTimer);
    }
  },
};
</script>

<style scoped>
.route-view-page-loading {
  position: relative;
  z-index: 1;
  padding: 48px 24px;
  text-align: center;
  color: #374151;
  font-size: 15px;
}
</style>

<style>
.route-view-fallback--portal {
  position: fixed;
  inset: 0;
  z-index: 200000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
  color: #111827;
  background: rgba(255, 255, 255, 0.96);
  pointer-events: auto;
}

.dark-mode .route-view-fallback--portal {
  color: #f3f4f6;
  background: rgba(17, 24, 39, 0.96);
}

.route-view-fallback--portal .route-view-fallback-path {
  margin-top: 8px;
  font-size: 13px;
  color: #6b7280;
  word-break: break-all;
}

.dark-mode .route-view-fallback--portal .route-view-fallback-path {
  color: #9ca3af;
}
</style>
